/**
 * Alarm sounds via WebAudio — IEC 60601-1-8 auditory alarm signals, synthesised
 * (no audio assets).
 *
 *   high    10 pulses: 3 + 2, pause, 3 + 2     pulse 150 ms, spacing x = 100 ms,
 *                                              3→4 gap = 2x + td, 5→6 gap = 0.5 s
 *   medium   3 pulses                          pulse 200 ms, spacing 200 ms
 *   low      2 pulses ("e c")                  pulse 200 ms, spacing 200 ms
 *
 * (Standard ranges: high pulse 75–200 ms, spacing 50–125 ms, 5→6 gap 0.35–1.3 s;
 * medium/low pulse 125–250 ms, spacing 125–250 ms.) Each pulse is a fundamental
 * (150–1000 Hz) plus four harmonics inside 300–4000 Hz, with rise and fall
 * ramps of ~15 % of the pulse — a bare sine "beep" does not meet the standard
 * and is much harder to localise. The melody of the burst comes from the alarm
 * CATEGORY (cardiac, oxygen, temperature, equipment…), see alarmConfig.SOUNDS.
 *
 * All tones route through a single master gain node. Muting sets that gain to
 * 0, which silences EVERYTHING instantly — including any pulse already
 * scheduled. playBurst() also early-returns when muted to avoid scheduling.
 *
 * Browsers keep an AudioContext suspended until the user interacts with the
 * page, so LiveMonitor calls armAudio() from a document-level click/keydown
 * listener; alarms before the first interaction are silent (visual still shows).
 */
import { SOUNDS, LOW_NOTES } from './alarmConfig';

const MUTE_KEY = 'lm-alerts-muted';
const C4 = 261.63;
const HARMONICS = [1, 0.5, 0.35, 0.25, 0.18];   // fundamental + 4 harmonics, all within ±15 dB
const PEAK = 0.22;

const TIMING = {
  high:   { td: 0.15, x: 0.10, mid: 0.50 },
  medium: { td: 0.20, y: 0.20 },
  low:    { td: 0.20, y: 0.20 },
};

let ctx = null;
let master = null;
let muted = localStorage.getItem(MUTE_KEY) === '1';

export function isMuted() { return muted; }

function applyMuteGain() {
  if (!ctx || !master) return;
  // Quick ramp (not an instant jump) to avoid a click, but effectively immediate.
  const now = ctx.currentTime;
  master.gain.cancelScheduledValues(now);
  master.gain.setTargetAtTime(muted ? 0 : 1, now, 0.01);
}

const muteListeners = new Set();
/** Be told when mute changes — including when ANOTHER tab/window changed it. */
export function onMuteChange(fn) { muteListeners.add(fn); return () => muteListeners.delete(fn); }

function applyMuted(m) {
  if (muted === m) return;
  muted = m;
  applyMuteGain();   // silence/unsilence anything currently scheduled, instantly
  muteListeners.forEach(fn => fn(muted));
}

export function setMuted(m) {
  localStorage.setItem(MUTE_KEY, m ? '1' : '0');   // fires a `storage` event in every OTHER tab
  applyMuted(!!m);
}

// Mute is ONE setting for this browser, not one per tab. Without this, muting
// the board in one window left every other open Live Monitor tab (a second
// screen, a forgotten background tab) sounding at full volume while the saved
// setting — and the button the user had just pressed — said "muted".
function syncFromStorage() {
  let stored = null;
  try { stored = localStorage.getItem(MUTE_KEY); } catch (e) { return; }
  if (stored != null) applyMuted(stored === '1');
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === MUTE_KEY || e.key === null) syncFromStorage(); });
  // Belt and braces: a tab that was frozen in the background may have missed
  // the event — re-read whenever it becomes visible or focused again.
  document.addEventListener('visibilitychange', syncFromStorage);
  window.addEventListener('focus', syncFromStorage);
}

export function armAudio() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = muted ? 0 : 1;   // honour persisted mute on first arm
    master.connect(ctx.destination);
  }
  if (ctx.state === 'suspended') ctx.resume();
}

export function audioReady() { return !!(ctx && master && ctx.state === 'running'); }

/** One pulse: fundamental + 4 harmonics under a shared rise/hold/fall envelope. */
function pulse(semitone, start, dur) {
  const f0 = C4 * Math.pow(2, semitone / 12);
  const env = ctx.createGain();
  const ramp = dur * 0.15;
  env.gain.setValueAtTime(0, start);
  env.gain.linearRampToValueAtTime(PEAK, start + ramp);
  env.gain.setValueAtTime(PEAK, start + dur - ramp);
  env.gain.linearRampToValueAtTime(0, start + dur);
  env.connect(master);   // through the master gain, not destination
  const norm = HARMONICS.reduce((a, b) => a + b, 0);
  HARMONICS.forEach((amp, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = f0 * (i + 1);
    g.gain.value = amp / norm;
    osc.connect(g).connect(env);
    osc.start(start);
    osc.stop(start + dur + 0.01);
  });
}

/** Start offsets (s) and notes of one burst for a priority + category sound. */
export function burstPlan(priority, sound) {
  const notes = (SOUNDS[sound] || SOUNDS.general).notes;
  const out = [];
  if (priority === 'high') {
    const { td, x, mid } = TIMING.high;
    let t = 0;
    for (let rep = 0; rep < 2; rep++) {
      out.push({ at: t, note: notes[0], dur: td }); t += td + x;
      out.push({ at: t, note: notes[1], dur: td }); t += td + x;
      out.push({ at: t, note: notes[2], dur: td }); t += td + (2 * x + td);
      out.push({ at: t, note: notes[3], dur: td }); t += td + x;
      out.push({ at: t, note: notes[4], dur: td }); t += td + mid;
    }
  } else if (priority === 'medium') {
    const { td, y } = TIMING.medium;
    for (let i = 0; i < 3; i++) out.push({ at: i * (td + y), note: notes[i], dur: td });
  } else {
    const { td, y } = TIMING.low;
    for (let i = 0; i < 2; i++) out.push({ at: i * (td + y), note: LOW_NOTES[i], dur: td });
  }
  return out;
}

/**
 * Play one alarm burst. Returns its length in ms (0 if nothing was scheduled),
 * so the caller can keep bursts from overlapping.
 */
export function playBurst(priority, sound) {
  if (muted || !audioReady()) return 0;
  const plan = burstPlan(priority, sound);
  const t0 = ctx.currentTime + 0.03;
  plan.forEach(p => pulse(p.note, t0 + p.at, p.dur));
  const last = plan[plan.length - 1];
  return Math.round((last.at + last.dur) * 1000);
}

/** Legacy helper (pre-IEC call sites): critical → high, anything else → medium. */
export function beep(severity) {
  return playBurst(severity === 'critical' ? 'high' : 'medium', 'general');
}
